import { useState } from 'react';
import PasswordChecklist from 'react-password-checklist';
import { cognitoResendConfirmationCode, cognitoSignIn, cognitoSignUp } from './cognito';
import VerificationModal from './verificationModal';
import InviteOnlyModal from './inviteOnlyModal';
import { FailedNotification } from './failedNotification';
import { useNavigate } from 'react-router-dom';
import { usePostAuthMutation } from '../../store/backendApi';
import { PageProps } from '../../shell/shell';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID!;
const COGNITO_REGION = 'eu-central-1';

export const LoginPage = (props: PageProps) => {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const [showInviteOnlyModal, setShowInviteOnlyModal] = useState(false);

  const [showFailureNotification, setShowFailureNotification] = useState(false);

  const [exchangeToken, { error: exchangeTokenError }] = usePostAuthMutation();

  const title = isSignup ? 'Register' : 'Login';
  const submitTitle = isSignup ? 'Sign up' : 'Login';
  const alternateText = isSignup ? 'Already have an account?' : "Don't have an account?";
  const alternateButton = isSignup ? 'Go to Login' : 'Go to Register';

  const exchangeTokens = async (idToken: string) => {
    await exchangeToken({
      tokenBodyRequest: {
        idToken: idToken,
      },
      rememberMe: true, // TODO: make optional via checkbox
    });
    if (exchangeTokenError) {
      console.warn(exchangeTokenError);
      alert(exchangeTokenError);
    } else {
      navigate('/');
    }
  };

  const submitAction = async (isSignup: boolean) => {
    if (!isSignup) {
      setProcessing(true);
      const result = await cognitoSignIn(COGNITO_REGION, COGNITO_CLIENT_ID, email, password);

      if (result.success) {
        // User successfully authenticated
        await exchangeTokens(result.data.IdToken!);
        setProcessing(false);
        return;
      } else {
        // Handle different error types
        setProcessing(false);
        if (result.error === 'UserNotConfirmedException') {
          await cognitoResendConfirmationCode(COGNITO_REGION, COGNITO_CLIENT_ID, email);
          setShowVerificationModal(true);
        } else {
          setShowFailureNotification(true);
        }
        return;
      }
    }
    if (localStorage.getItem('allow_register') === 'true') {
      if (await cognitoSignUp(COGNITO_REGION, COGNITO_CLIENT_ID, email, password)) {
        setShowVerificationModal(true);
      } else {
        alert('sign up failed!');
      }
    } else {
      setShowInviteOnlyModal(true);
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img alt="TripVibes" src="/logo_full.png" className="mx-auto h-28 w-auto" />
          <h2 className="mt-16 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            {title}
          </h2>
        </div>

        <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
          <form
            className="space-y-6"
            onSubmit={(e) => {
              if (!passwordValid) e.preventDefault();
            }}
          >
            <div>
              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                Email address
              </label>
              <div className="mt-2">
                <input
                  disabled={isProcessing}
                  id="email"
                  name="email"
                  type="email"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                  outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                  focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                  Password
                </label>
                {!isSignup && (
                  <div className="text-sm">
                    <a href="#" className="font-semibold text-indigo-600 hover:text-indigo-500">
                      Forgot password?
                    </a>
                  </div>
                )}
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  disabled={isProcessing}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                  outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                  focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 ${
    !isSignup || passwordValid
      ? 'focus:outline-indigo-600'
      : 'outline-red-500 ' + 'focus:outline-red-600'
    }`}
                />
              </div>
            </div>

            {isSignup && (
              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password-repeat"
                    className="block text-sm/6 font-medium text-gray-900"
                  >
                    Repeat Password
                  </label>
                </div>
                <div className="mt-2">
                  <input
                    disabled={isProcessing}
                    id="password-repeat"
                    name="password-repeat"
                    type="password"
                    required={isSignup}
                    value={passwordRepeat}
                    onChange={(e) => setPasswordRepeat(e.target.value)}
                    autoComplete="current-password"
                    className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900
                    outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline
                    focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 ${
              !isSignup || passwordValid
                ? 'focus:outline-indigo-600'
                : 'outline-red-500 ' + 'focus:outline-red-600'
              }`}
                  />
                </div>
                <PasswordChecklist
                  rules={['minLength', 'specialChar', 'number', 'capital', 'match']}
                  minLength={5}
                  value={password}
                  valueAgain={passwordRepeat}
                  onChange={(isValid) => {
                    setPasswordValid(isValid);
                  }}
                />
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isProcessing}
                className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6
                font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={(e) => {
                  e.preventDefault();
                  const form = (e.target as HTMLButtonElement).closest('form') as HTMLFormElement;
                  if (form.reportValidity() && (!isSignup || passwordValid)) {
                    submitAction(isSignup);
                  }
                }}
              >
                {isProcessing ? 'loading... ' : submitTitle}
              </button>
            </div>
          </form>
          <p className="mt-10 text-center text-sm/6 text-gray-500">{alternateText}</p>
          <div>
            <button
              type="button"
              disabled={isProcessing}
              className="flex w-full justify-center rounded-md bg-indigo-200 px-3 py-1.5 text-sm/6
              font-semibold text-black shadow-sm hover:bg-indigo-300 focus-visible:outline
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-200"
              onClick={() => setIsSignup((prevState) => !prevState)}
            >
              {alternateButton}
            </button>
          </div>
          <FailedNotification
            show={showFailureNotification}
            setShow={setShowFailureNotification}
            title={'Login failed'}
            body={'Wrong username or password'}
          />
          <InviteOnlyModal open={showInviteOnlyModal} setOpen={setShowInviteOnlyModal} />
          {showVerificationModal && (
            <VerificationModal
              clientId={COGNITO_CLIENT_ID}
              region={COGNITO_REGION}
              email={email}
              loginFunc={async () => {
                await submitAction(false);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};
